import Accordion from '@allesauto/core/accordion';
import AccordionFaq, {
  AccordionFaqAdapter,
} from '@allesauto/core/AccordionFaq';
import BenefitsTeaser, {
  BenefitsTeaserAdapter,
} from '@allesauto/core/benefitTeaser';
import Brand from '@allesauto/core/Brand';
import CheckListContainer, {
  CheckListContainerAdapter,
} from '@allesauto/core/checklistContainer';
import ContentSpacer from '@allesauto/core/contentSpacer';
import ContentSteps from '@allesauto/core/ContentSteps';
import CtaTeaser from '@allesauto/core/ctaTeaser';
import DownloadPdfButton from '@allesauto/core/downloadPdfButton';
import FilterBoxCarTypes, {
  ICarTypesProps,
} from '@allesauto/core/filterBoxCarTypes';
import FilterBoxPrice, {
  ICarPriceFilterItem,
} from '@allesauto/core/filterBoxPrice';
import Heading from '@allesauto/core/heading';
import HeroStage from '@allesauto/core/heroStage';
import ImageStage from '@allesauto/core/imageStage';
import ImageTextStep from '@allesauto/core/imageTextStep';
import LatestVehiclesTeaser, {
  LatestVehiclesTeaserProps,
} from '@allesauto/core/latestVehiclesTeaser';
import NewsTeaser from '@allesauto/core/newsTeaser';
import PressComponent from '@allesauto/core/pressComponent';
import SimpleContactForm from '@allesauto/core/simpleContactForm';
import SimpleImageComponent from '@allesauto/core/simpleImageComponent';
import SimpleVideoComponent from '@allesauto/core/simpleVideoComponent';
import SocialMediaTeaser from '@allesauto/core/socialMediaTeaser';
import StepComponent from '@allesauto/core/stepComponent';
import StepComponentIntro from '@allesauto/core/stepComponentIntro';
import Testimonial from '@allesauto/core/testimonial';
import TestimonialCarousel from '@allesauto/core/testimonialCarousel';
import TextComponent, {
  TextComponentAdapter,
} from '@allesauto/core/textComponent';
import TextComponentFullWidth from '@allesauto/core/textComponentFullwidth';
import TextComponentLegacy from '@allesauto/core/textComponentLegacy';
import TilesGrid from '@allesauto/core/tilesGrid';
import TitleStage from '@allesauto/core/titleStage';
import VideoStage from '@allesauto/core/videoStage';
import { useRouter } from 'next/router';
import React, { FC } from 'react';

import { FilterCarBrands } from '@/../../../packages/allesauto-core/src';
import { FilterCarBrandsItems } from '@/../../../packages/allesauto-core/src/lib/filterCarBrands';
import TextComponentContainer from '@/global/containers/TextComponentContainer';

import Placeholder from './Placeholder';
import CarFinder from '../CarFinder/CarFinder';
import CarouselHybridComponent from '../CarouselHybridComponent/CarouselHybridComponent';
import CodeBlockComponent from '../CodeBlockComponent/CodeBlockComponent';
import DealerOverview from '../DealerOverview/DealerOverview';
import NewsletterStandaloneForm from '../forms/NewsletterForms/NewsletterStandaloneForm';
import NewsletterTeaser from '../forms/NewsletterForms/NewsletterTeaserForm';
import HeroStageWithWidget from '../HeroStageWithWidget/HeroStageWithWidget';
import { VehicleSearchFormProps } from '../QuickEntryWidget/VehicleSearchForm/VehicleSearchForm';
import BannerBoxPictureText from '../Storyblok/BannerBoxPictureText';
import BrandModelsListing from '../Storyblok/BrandModelsListing';
import DealerZipcodeTeaser from '../Storyblok/DealerZipcodeTeaser';
import FilterBoxIndividual from '../Storyblok/FilterboxIndividual';
import GridBoxesImageText from '../Storyblok/GridBoxesImageText';
import ImageGallery from '../Storyblok/ImageGallery';
import ImageLogoSlider from '../Storyblok/ImageLogoSlider';
import ProsCons from '../Storyblok/ProsCons';
import TextboxTextImage from '../Storyblok/TextboxTextImage';
import TextChecklistPicture from '../Storyblok/TextChecklistPicture';
import Textslider from '../Storyblok/Textslider';
import WhitepaperDownload from '../Storyblok/WhitepaperDownload';

export interface DynamicComponentProps {
  component: string;
  [key: string]: any;
  carFinderData?: VehicleSearchFormProps;
  latestVehicles?: Pick<LatestVehiclesTeaserProps, 'vehicles'>;
  filterCarPriceItems?: ICarPriceFilterItem[];
  filterCarTypeItems?: ICarTypesProps[];
  filterCarBrandsItems?: FilterCarBrandsItems[];
  _uid: string;
  _editable?: string;
}

const Components: Record<string, React.ElementType> = {
  accordion: Accordion,
  'benefits-teaser': BenefitsTeaserAdapter(BenefitsTeaser),
  'check-list-container': CheckListContainerAdapter(CheckListContainer),
  'carousel-component': CarouselHybridComponent,
  'contact-component': SimpleContactForm,
  'content-spacer': ContentSpacer,
  'content-steps': ContentSteps,
  'cta-teaser': CtaTeaser,
  // CarFinder is a hybrid component that uses data from both Storyblok and an API
  'car-finder': CarFinder,
  'image-text-step': ImageTextStep,
  'image-stage': ImageStage,
  'video-stage': VideoStage,
  'news-teaser': NewsTeaser,
  'newsletter-teaser': NewsletterTeaser,
  'newsletter-standalone-form': NewsletterStandaloneForm,
  'press-component': PressComponent,
  'testimonial-carousel': TestimonialCarousel,
  testimonial: Testimonial,
  'text-component': TextComponentAdapter(TextComponent),
  'text-component-fullwidth': TextComponentFullWidth,
  // Legacy TextComponent supports the deprecated structure from static JSON files
  'text-component-legacy': TextComponentLegacy,
  'text-component-overlay': TextComponentContainer,
  brand: Brand,
  hero: HeroStage,
  'hero-stage-with-widget': HeroStageWithWidget,
  'step-component': StepComponent,
  'step-component-intro': StepComponentIntro,
  tiles: TilesGrid,
  'faq-accordion': AccordionFaqAdapter(AccordionFaq),
  'latest-vehicle-teaser': LatestVehiclesTeaser,
  'dealer-overview': DealerOverview,
  'simple-video': SimpleVideoComponent,
  'simple-image': SimpleImageComponent,
  'social-media': SocialMediaTeaser,
  'title-stage': TitleStage,
  boxPriceSection: FilterBoxPrice,
  boxCarTypes: FilterBoxCarTypes,
  textboxTextImage: TextboxTextImage,
  brandsAndModelsListing: BrandModelsListing,
  whitepaperDownload: WhitepaperDownload,
  boxIndividual: FilterBoxIndividual,
  heading: Heading,
  downloadPdfButton: DownloadPdfButton,
  'image-gallery': ImageGallery,
  carBrands: FilterCarBrands,
  prosCons: ProsCons,
  bannerBoxPictureText: BannerBoxPictureText,
  textChecklistPicture: TextChecklistPicture,
  gridBoxesImageText: GridBoxesImageText,
  textslider: Textslider,
  plzFinder: DealerZipcodeTeaser,
  imageLogoSlider: ImageLogoSlider,
  code: CodeBlockComponent,
};

const DynamicComponent: FC<React.PropsWithChildren<DynamicComponentProps>> = (
  props: DynamicComponentProps
) => {
  // carFinderData contains vehicle data that is coming from an API and is only used to the car-finder component
  const {
    component = '',
    carFinderData = undefined,
    latestVehicles = undefined,
    filterCarPriceItems = undefined,
    filterCarTypeItems = undefined,
    filterCarBrandItems = undefined,
    filterCarIndividualItems = undefined,
  } = props;
  const Component = Components[component];
  const router = useRouter();

  const createCtaAction = (props: DynamicComponentProps) => {
    const { ctaAction } = props;

    let handleCta = () => {
      // initialise function
    };

    if (ctaAction) {
      if (ctaAction.substring(0, 3) === 'http') {
        handleCta = () => window.open(ctaAction, '_blank');
      } else if (ctaAction.substring(0, 1) === '/') {
        handleCta = () => router.push(ctaAction);
      }
    }

    return {
      ...props,
      handleCta,
      vehicleSearchForm: carFinderData ?? {},
      latestVehicles: latestVehicles ?? [],
      filterCarPriceItems: filterCarPriceItems ?? [],
      filterCarTypeItems: filterCarTypeItems ?? [],
      filterCarBrandItems: filterCarBrandItems ?? [],
      filterCarIndividualItems: filterCarIndividualItems ?? [],
    };
  };

  const componentProps = (props: DynamicComponentProps) =>
    createCtaAction(props);

  return (
    (Components[component] && <Component {...componentProps(props)} />) || (
      <Placeholder componentId={component} />
    )
  );
};

export default DynamicComponent;
